import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle } from "../Drawer";
import { Saml2ServiceProviderDetailsDrawerProps, Saml2ServiceProviderDetailsDrawerState } from "./Saml2ServiceProviderDetailsDrawer.types";
import { findSaml2ServiceProviderAsync, getCancelTokenSource } from "src/api";
import { CaptionBolder, SmallBody } from "../Text";
import { localize } from "src/l10n";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { InfoBlock, InfoBlockList, InfoBlockProperty } from "../InfoBlock";
import { Conditional } from "../Conditional";
import SpintrLoader from "src/ui/components/Loader";
import { SpintrTypes } from "src/typings";
import { TextField } from "@fluentui/react";
import { useSelector } from "react-redux";

/**
 * Displays the details of a SAML2 service provider.
 * 
 * @param param0 The properties of the component.
 * @returns The element to render.
 */
function Saml2ServiceProviderDetailsDrawer({
    onClose,
    open,
    serviceProviderId,
}: Saml2ServiceProviderDetailsDrawerProps): ReactElement {
    const asdasd = useSelector<Spintr.AppState>((appState) => appState.instance.get("id"));
    console.log(asdasd);

    const [state, setState] = useState<Saml2ServiceProviderDetailsDrawerState>({
        isLoading: false,
        serviceProvider: undefined,
    });

    const onOpenChanged = useCallback(
        (open: boolean) => {
            if (open || !onClose) {
                return;
            }

            onClose();
        },
        [onClose]);

    useEffect(
        function _Saml2ServiceProviderDetailsPanelMount() {
            if (!open || !serviceProviderId) {
                return;
            }

            const cancelTokenSource = getCancelTokenSource();

            findSaml2ServiceProviderAsync(serviceProviderId, cancelTokenSource.token)
                .then((serviceProvider) => setState((prevState) => ({
                    ...prevState,
                    serviceProvider,
                    isLoading: false,
                })))
                .catch((error) => {
                    console.error(error);
                    onClose();
                });

            return function _Saml2ServiceProviderDetailsPanelUnmount() {
                cancelTokenSource.cancel();
            }
        },
        [onClose, open, serviceProviderId, setState]);

    const { isLoading, serviceProvider } = state;
        
    return (
        <Drawer position="end" onOpenChange={onOpenChanged} open={open} size="medium">
            <DrawerHeader>
                <DrawerHeaderTitle>{serviceProvider?.name}</DrawerHeaderTitle>
            </DrawerHeader>

            <DrawerBody>
                <Conditional condition={isLoading}>
                    <SpintrLoader />
                </Conditional>

                <Conditional condition={!isLoading && serviceProvider}>
                    <InfoBlock heading={localize("DETAILS")}>
                        <InfoBlockProperty name={localize("SAML_ENTITY_ID")}>
                            {serviceProvider?.entityId}
                        </InfoBlockProperty>

                        <InfoBlockProperty name={localize("SAML_SSO_URL")}>
                            <ul>
                                {(serviceProvider?.assertionConsumerServiceUrls ?? []).map((url, index) => (
                                    <li key={index}>
                                        {url}
                                    </li>
                                ))}
                            </ul>
                        </InfoBlockProperty>

                        <InfoBlockProperty name={localize("SAML_SLO_URL")}>
                            {serviceProvider?.singleLogoutServiceUrl}
                        </InfoBlockProperty>

                        <InfoBlockProperty name={localize("SAML_METADATA_URL")}>
                            {serviceProvider?.metadataUrl}
                        </InfoBlockProperty>
                    </InfoBlock>

                    <InfoBlock heading={localize("CERTIFICATES")}>

                        <InfoBlockProperty name={localize("SAML_METADATA_URL")}>
                            <TextField
                                disabled={true}
                                value={`https://authentication.spintr.me/saml2/${serviceProvider?.instanceId}/metadata.xml?appId=${serviceProvider?.id}`} />
                        </InfoBlockProperty>

                        <InfoBlockList<Spintr.Saml2Certificate>
                            columns={[{
                                key: "name",
                                text: localize("NAME"),
                            }, {
                                key: "thumbprint",
                                text: localize("THUMBPRINT"),
                            }]}
                            data={(serviceProvider?.certificates ?? [])
                                .filter((certificate) => 
                                    SpintrTypes.SamlCertificateType.IdentityProviderEncryption === certificate.certificateType ||
                                    SpintrTypes.SamlCertificateType.IdentityProviderSigning === certificate.certificateType)}
                        />
                    </InfoBlock>

                    <InfoBlock heading={localize("SETUP")}>

                        <InfoBlockProperty name={localize("SAML_LOGIN_URL")}>
                            <TextField
                                disabled={true}
                                value={`https://authentication.spintr.me/saml2/${serviceProvider?.instanceId}/sso/redirect`} />
                        </InfoBlockProperty>

                    </InfoBlock>
                </Conditional>
            </DrawerBody>
        </Drawer>
    );
}

export default Saml2ServiceProviderDetailsDrawer;
